/* eslint-disable */
import { useState, useEffect, useRef, useCallback } from 'react';

// ─── API ──────────────────────────────────────────────────────────────────────
var cardCache = {};
// Name normalisation map — common deck list shorthand -> API name
var NAME_MAP = {
  'Nidoran F':    'Nidoran ♀',
  'Nidoran M':    'Nidoran ♂',
  'Nidoran-F':    'Nidoran ♀',
  'Nidoran-M':    'Nidoran ♂',
  'Mr. Mime':     'Mr. Mime',
  'Farfetch\'d': "Farfetch'd",
};

async function fetchCardData(cardName, setName) {
  // Normalise name before searching
  cardName = NAME_MAP[cardName] || cardName;
  var cacheKey = cardName + (setName ? '::' + setName : '');
  if (cardCache[cacheKey]) return cardCache[cacheKey];
  try {
    // Sanitize name: remove quotes but keep apostrophes as-is for names like "Team Magma's Zangoose"
    // The API supports apostrophes in names natively
    var clean = cardName.trim();

    // Try 1: name + set (most precise)
    if (setName) {
      var sn = setName.trim();
      var q1 = 'name:"' + clean + '" set.name:"' + sn + '"';
      var url1 = 'https://api.pokemontcg.io/v2/cards?q=' + encodeURIComponent(q1) + '&pageSize=8&orderBy=set.releaseDate';
      var r1 = await fetch(url1);
      if (r1.ok) {
        var j1 = await r1.json();
        if (j1.data && j1.data.length > 0) {
          var snLow = sn.toLowerCase();
          var exact = j1.data.find(function(c){return c.set&&c.set.name&&c.set.name.toLowerCase()===snLow;});
          var best1 = exact || j1.data[0];
          cardCache[cacheKey] = best1; return best1;
        }
      }
    }

    // Try 2: name only with wildcard (handles apostrophes better)
    var q2 = 'name:"' + clean + '"';
    var order2 = setName ? 'set.releaseDate' : '-set.releaseDate';
    var url2 = 'https://api.pokemontcg.io/v2/cards?q=' + encodeURIComponent(q2) + '&pageSize=12&orderBy=' + order2;
    var r2 = await fetch(url2);
    if (r2.ok) {
      var j2 = await r2.json();
      if (j2.data && j2.data.length > 0) {
        var best2 = j2.data[0];
        // If we have a set name preference, find closest match by year
        if (setName) {
          var snLow2 = setName.toLowerCase();
          var exactBySet = j2.data.find(function(c){return c.set&&c.set.name&&c.set.name.toLowerCase()===snLow2;});
          if (exactBySet) best2 = exactBySet;
        }
        cardCache[cacheKey] = best2; return best2;
      }
    }

    // Try 3: strip possessive apostrophe and retry (e.g. "Team Magma s Zangoose")
    if (clean.includes("'")) {
      var stripped = clean.replace(/'/g, '');
      var q3 = 'name:"' + stripped + '"';
      var url3 = 'https://api.pokemontcg.io/v2/cards?q=' + encodeURIComponent(q3) + '&pageSize=4&orderBy=-set.releaseDate';
      var r3 = await fetch(url3);
      if (r3.ok) {
        var j3 = await r3.json();
        if (j3.data && j3.data.length > 0) {
          cardCache[cacheKey] = j3.data[0]; return j3.data[0];
        }
      }
    }

  } catch(e){ console.warn('fetchCardData error for', cardName, e); }
  return null;
}

function mergeApiCard(card,api){
  // Always trust API supertype above all else
  var supertype = api.supertype || card.supertype;
  var isPoke = supertype==='Pokemon' || supertype==='Pokémon';
  var isTrain = supertype==='Trainer';
  var isEn = supertype==='Energy';
  return Object.assign({},card,{
    supertype: supertype,
    subtypes:  api.subtypes  || card.subtypes  || [],
    images:    api.images    || card.images    || {},
    rules:     api.rules     || card.rules     || [],
    evolvesFrom: isPoke ? (api.evolvesFrom || null) : null,
    // Pokemon fields — use API data, NEVER fall back to stub guesses
    hp:          isPoke ? (api.hp  || null) : null,
    attacks:     isPoke ? (api.attacks   || []) : [],
    abilities:   isPoke ? (api.abilities || []) : [],
    weaknesses:  isPoke ? (api.weaknesses  || []) : [],
    resistances: isPoke ? (api.resistances || []) : [],
    retreatCost: isPoke ? (api.retreatCost || []) : [],
    // Types: Pokemon gets real types, Trainers/Energy get empty (no "Colorless" badges)
    types:       isPoke ? (api.types || []) : [],
  });
}

async function enrichDeckCards(deckCards){
  return Promise.all(deckCards.map(async function(entry){
    var card=entry.card;
    // Only skip enrichment if the card has REAL images from the API (not an empty {} stub)
    // A real image URL starts with https://images.pokemontcg.io
    var hasRealImage = card.images &&
      ((card.images.small && card.images.small.startsWith('https://images.pokemontcg')) ||
       (card.images.large && card.images.large.startsWith('https://images.pokemontcg')));
    if (hasRealImage) return entry; // already fully enriched from live card repo browse
    // Fetch from API using the set name stored on the entry (worlds decks carry this)
    var setName = entry.setName || card.setName || null;
    var api = await fetchCardData(card.name, setName);
    if (!api) return entry;
    return { qty:entry.qty, card:mergeApiCard(card,api) };
  }));
}

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function shuffle(arr){var a=arr.slice();for(var i=a.length-1;i>0;i--){var j=Math.floor(Math.random()*(i+1));var t=a[i];a[i]=a[j];a[j]=t;}return a;}
function clone(o){return JSON.parse(JSON.stringify(o));}
function getHP(c){var n=parseInt(c.hp);return(!isNaN(n)&&n>0)?n:60;}
function ecost(cost){if(!cost)return 0;return cost.filter(function(c){return c!=='Free';}).length;}
function countE(arr){return arr?arr.length:0;}
function canAtk(atk,ec){return ecost(atk.cost)<=ec;}
function atkDmg(atk){if(!atk.damage)return 0;var d=parseInt(atk.damage.replace(/[^0-9]/g,''));return isNaN(d)?0:d;}
function calcDmg(atk,attacker,defender){
  var base=atkDmg(atk);if(base===0)return 0;
  var at=attacker.card.types||[],mult=1,red=0;
  (defender.card.weaknesses||[]).forEach(function(w){if(at.indexOf(w.type)!==-1)mult*=w.value&&w.value.includes('x')?(parseFloat(w.value.replace('x',''))||2):2;});
  (defender.card.resistances||[]).forEach(function(r){if(at.indexOf(r.type)!==-1)red+=Math.abs(parseInt(r.value)||30);});
  return Math.max(0,Math.floor(base*mult)-red);
}
function isBenchFull(b){return b.length>=5;}
function isBasic(c){return(c.supertype==='Pokemon'||c.supertype==='Pokémon')&&c.subtypes&&c.subtypes.indexOf('Basic')!==-1;}
function isStage1(c){return(c.supertype==='Pokemon'||c.supertype==='Pokémon')&&c.subtypes&&c.subtypes.indexOf('Stage 1')!==-1;}
function isStage2(c){return(c.supertype==='Pokemon'||c.supertype==='Pokémon')&&c.subtypes&&c.subtypes.indexOf('Stage 2')!==-1;}
function isPokemon(c){return c.supertype==='Pokemon'||c.supertype==='Pokémon';}
function isTrainer(c){return c.supertype==='Trainer';}
function isEnergy(c){return c.supertype==='Energy';}
function isSupporter(c){return isTrainer(c)&&c.subtypes&&c.subtypes.indexOf('Supporter')!==-1;}
function isStadium(c){return isTrainer(c)&&c.subtypes&&c.subtypes.indexOf('Stadium')!==-1;}
function isTool(c){return isTrainer(c)&&c.subtypes&&(c.subtypes.indexOf('Pokémon Tool')!==-1||c.subtypes.indexOf('Pokemon Tool')!==-1||c.subtypes.indexOf('Tool')!==-1);}
function isItem(c){return isTrainer(c)&&!isSupporter(c)&&!isStadium(c)&&!isTool(c);}

// ─── TRAINER EFFECTS ──────────────────────────────────────────────────────────
function applyTrainer(card,gs,extra){
  var name=card.name.toLowerCase(),log=[];
  if(name.includes('rare candy')){
    if(!extra){
      var opts=[];
      var inPlay=[];
      if(gs.player.active)inPlay.push({poke:gs.player.active,loc:'active',idx:-1});
      gs.player.bench.forEach(function(p,i){inPlay.push({poke:p,loc:'bench',idx:i});});
      gs.player.hand.forEach(function(c,hi){
        if(!isStage2(c))return;
        inPlay.forEach(function(slot){
          var chain=getChain(c);
          if(chain.length>=3&&chain[2].toLowerCase()===slot.poke.card.name.toLowerCase())
            opts.push({cardIdx:hi,card:c,slot:slot,chain:chain});
        });
      });
      if(!opts.length)return{ok:false,msg:'No valid Rare Candy target. Need Stage 2 in hand and its Basic in play.'};
      return{ok:true,needsTarget:{type:'rare_candy',options:opts},log:[]};
    }
    var ec=gs.player.hand[extra.cardIdx];
    if(!ec)return{ok:false,msg:'Card not found.'};
    gs.player.hand.splice(extra.cardIdx,1);
    if(extra.slot.loc==='active'){var old=gs.player.active;gs.player.active={card:ec,currentHP:getHP(ec),maxHP:getHP(ec),energy:clone(old.energy),statusCondition:null};log.push('Rare Candy: '+old.card.name+' → '+ec.name+'!');}
    else{var b=gs.player.bench[extra.slot.idx];gs.player.bench[extra.slot.idx]={card:ec,currentHP:getHP(ec),maxHP:getHP(ec),energy:clone(b.energy),statusCondition:null};log.push('Rare Candy: '+b.card.name+' → '+ec.name+'!');}
    gs.player.discard.push(card);return{ok:true,log:log,gs:gs};
  }
  if(/professor|juniper|sycamore|research/.test(name)){
    var disc=gs.player.hand.length;gs.player.discard=gs.player.discard.concat(gs.player.hand);gs.player.hand=[];
    var dr=gs.player.deck.splice(0,7);gs.player.hand=dr;gs.player.discard.push(card);
    log.push(card.name+': discarded '+disc+', drew '+dr.length+'.');return{ok:true,log:log,gs:gs};
  }
  if(name==='n'){
    gs.player.deck=shuffle(gs.player.deck.concat(gs.player.hand));gs.player.hand=[];
    var nd=gs.player.deck.splice(0,gs.player.prizes.length);gs.player.hand=nd;gs.player.discard.push(card);
    log.push('N: drew '+nd.length+'.');return{ok:true,log:log,gs:gs};
  }
  if(/marnie|iono|copycat|judge/.test(name)){
    gs.player.deck=shuffle(gs.player.deck.concat(gs.player.hand));gs.player.hand=[];
    var md=gs.player.deck.splice(0,5);gs.player.hand=md;gs.player.discard.push(card);
    log.push(card.name+': drew '+md.length+'.');return{ok:true,log:log,gs:gs};
  }
  if(/cynthia|lillie|bianca/.test(name)){
    var cd=gs.player.deck.splice(0,6);gs.player.hand=gs.player.hand.concat(cd);gs.player.discard.push(card);
    log.push(card.name+': drew '+cd.length+'.');return{ok:true,log:log,gs:gs};
  }
  if(/skyla|arven|celio|bebe|roseanne/.test(name)){
    if(!gs.player.deck.length)return{ok:false,msg:'Deck empty!'};
    var sd=gs.player.deck.splice(0,1);gs.player.hand=gs.player.hand.concat(sd);gs.player.discard.push(card);
    log.push(card.name+': searched and drew '+sd[0].name+'.');return{ok:true,log:log,gs:gs};
  }
  if(/ultra ball|poke ball|nest ball|great ball|level ball|dive ball/.test(name)){
    var pi=gs.player.deck.findIndex(isPokemon);
    if(pi!==-1){var pc=gs.player.deck.splice(pi,1)[0];gs.player.hand.push(pc);log.push(card.name+': found '+pc.name+'.');}
    else log.push(card.name+': no Pokémon in deck.');
    gs.player.discard.push(card);return{ok:true,log:log,gs:gs};
  }
  if(/switch|escape rope|warp/.test(name)){
    if(!gs.player.active||!gs.player.bench.length)return{ok:false,msg:'Need active + benched Pokémon.'};
    var sw=gs.player.active;gs.player.active=gs.player.bench.shift();gs.player.bench.push(sw);gs.player.discard.push(card);
    log.push(card.name+': switched to '+gs.player.active.card.name+'.');return{ok:true,log:log,gs:gs};
  }
  if(/boss|lysandre|guzma|catcher/.test(name)){
    if(!gs.ai.bench.length)return{ok:false,msg:'AI has no benched Pokémon.'};
    var gOld=gs.ai.active;gs.ai.active=gs.ai.bench.shift();if(gOld)gs.ai.bench.push(gOld);gs.player.discard.push(card);
    log.push(card.name+': forced '+gs.ai.active.card.name+' to active!');return{ok:true,log:log,gs:gs};
  }
  if(/vs seeker|pal pad/.test(name)){
    var vi=gs.player.discard.findIndex(isSupporter);
    if(vi!==-1){gs.player.hand.push(gs.player.discard.splice(vi,1)[0]);log.push(card.name+': retrieved supporter.');}
    else log.push(card.name+': no supporter in discard.');
    gs.player.discard.push(card);return{ok:true,log:log,gs:gs};
  }
  if(/dark patch|max elixir|energy retrieval/.test(name)){
    var ei2=gs.player.discard.findIndex(isEnergy);
    if(ei2!==-1&&gs.player.active){var en2=gs.player.discard.splice(ei2,1)[0];gs.player.active.energy.push(en2);log.push(card.name+': attached '+en2.name+' from discard.');}
    else log.push(card.name+': no energy to attach.');
    gs.player.discard.push(card);return{ok:true,log:log,gs:gs};
  }
  if(/potion|gold berry|full heal/.test(name)){
    if(!gs.player.active)return{ok:false,msg:'No active to heal.'};
    var h=name.includes('gold')||name.includes('full')?60:30;
    gs.player.active.currentHP=Math.min(gs.player.active.maxHP,gs.player.active.currentHP+h);
    gs.player.discard.push(card);log.push(card.name+': healed '+h+' HP.');return{ok:true,log:log,gs:gs};
  }
  if(/super rod|night maintenance/.test(name)){
    var rec=0;for(var ri=gs.player.discard.length-1;ri>=0&&rec<3;ri--){if(isPokemon(gs.player.discard[ri])||isEnergy(gs.player.discard[ri])){gs.player.deck.push(gs.player.discard.splice(ri,1)[0]);rec++;}}
    gs.player.deck=shuffle(gs.player.deck);gs.player.discard.push(card);log.push(card.name+': recovered '+rec+' cards.');return{ok:true,log:log,gs:gs};
  }
  // Default
  var def=gs.player.deck.splice(0,1);gs.player.hand=gs.player.hand.concat(def);gs.player.discard.push(card);
  log.push(card.name+(def.length?': drew 1 card.':'.'));return{ok:true,log:log,gs:gs};
}

var EVOLVE_MAP={'Combusken':'Torchic','Grovyle':'Treecko','Marshtomp':'Mudkip','Kirlia':'Ralts','Kadabra':'Abra','Haunter':'Gastly','Dragonair':'Dratini','Metang':'Beldum','Charmeleon':'Charmander','Wartortle':'Squirtle','Ivysaur':'Bulbasaur','Drizzile':'Sobble','Brionne':'Popplio','Dartrix':'Rowlet','Braixen':'Fennekin','Frogadier':'Froakie','Servine':'Snivy','Dewott':'Oshawott','Pignite':'Tepig','Pidgeotto':'Pidgey','Gabite':'Gible','Shelgon':'Bagon','Zweilous':'Deino'};
function getChain(card){var ch=[card.name];if(card.evolvesFrom){ch.push(card.evolvesFrom);var b=EVOLVE_MAP[card.evolvesFrom];if(b)ch.push(b);}return ch;}

// ─── GAME STATE ───────────────────────────────────────────────────────────────
function buildState(deckCards,name){
  var full=[];deckCards.forEach(function(e){for(var i=0;i<e.qty;i++)full.push(clone(e.card));});
  full=shuffle(full);
  var hand=full.splice(0,7),attempts=0;
  while(!hand.some(isBasic)&&attempts<10){full=shuffle(full.concat(hand));hand=full.splice(0,7);attempts++;}
  var prizes=full.splice(0,6);
  return{name:name,deck:full,hand:hand,prizes:prizes,discard:[],active:null,bench:[],stadium:null,trainerSlot:null,hasDrawn:false,hasAttachedEnergy:false,hasRetreated:false,hasPlayedSupporter:false,hasPlayedTool:false};
}
function makeInPlay(card){return{card:clone(card),currentHP:getHP(card),maxHP:getHP(card),energy:[],statusCondition:null};}

function aiSetup(ai){
  var bi=ai.hand.findIndex(isBasic);
  if(bi!==-1)ai.active=makeInPlay(ai.hand.splice(bi,1)[0]);
  ai.hand.filter(isBasic).forEach(function(c){if(!isBenchFull(ai.bench)){var i=ai.hand.findIndex(function(x){return x.id===c.id;});if(i!==-1)ai.bench.push(makeInPlay(ai.hand.splice(i,1)[0]));}});
  return ai;
}
function aiTurn(ai){
  var log=[];
  ai.hand.filter(isBasic).forEach(function(c){if(!isBenchFull(ai.bench)){var i=ai.hand.findIndex(function(x){return x.id===c.id;});if(i!==-1){ai.bench.push(makeInPlay(ai.hand.splice(i,1)[0]));log.push('AI benched '+c.name+'.');}}});
  if(!ai.active&&ai.bench.length>0){ai.active=ai.bench.shift();log.push('AI promoted '+ai.active.card.name+'.');}
  if(ai.active&&(ai.turn||0)>1){var evo=ai.hand.findIndex(function(c){return isPokemon(c)&&(isStage1(c)||isStage2(c))&&c.evolvesFrom&&c.evolvesFrom.toLowerCase()===ai.active.card.name.toLowerCase();});if(evo!==-1){var ec=ai.hand.splice(evo,1)[0];var old=ai.active;ai.active={card:ec,currentHP:getHP(ec),maxHP:getHP(ec),energy:clone(old.energy),statusCondition:null};log.push('AI evolved '+old.card.name+' → '+ec.name+'!');}}
  if(!ai.hasAttachedEnergy){var ei=ai.hand.findIndex(isEnergy);if(ei!==-1&&ai.active){var en=ai.hand.splice(ei,1)[0];ai.active.energy.push(en);ai.hasAttachedEnergy=true;log.push('AI attached '+en.name+'.');}}
  if(!ai.hasPlayedSupporter){var si=ai.hand.findIndex(isSupporter);if(si!==-1){var sup=ai.hand.splice(si,1)[0];ai.discard.push(sup);var d=ai.deck.splice(0,4);ai.hand=ai.hand.concat(d);ai.hasPlayedSupporter=true;log.push('AI played '+sup.name+'.');}}
  return{ai:ai,log:log};
}
function aiAtk(ai,player){if(!ai.active||!player.active)return null;var ec=countE(ai.active.energy);var u=(ai.active.card.attacks||[]).filter(function(a){return canAtk(a,ec);});if(!u.length)return null;return u.sort(function(a,b){return atkDmg(b)-atkDmg(a);})[0];}

// ─── COLOURS ──────────────────────────────────────────────────────────────────
var TC={Fire:'#ef4444',Water:'#3b82f6',Grass:'#22c55e',Lightning:'#eab308',Psychic:'#a855f7',Fighting:'#f97316',Darkness:'#818cf8',Metal:'#94a3b8',Dragon:'#4f46e5',Fairy:'#ec4899',Colorless:'#9ca3af'};
var EE={Fire:'🔥',Water:'💧',Grass:'🌿',Lightning:'⚡',Psychic:'🔮',Fighting:'👊',Darkness:'🌑',Metal:'⚙️',Dragon:'🐲',Fairy:'✨',Colorless:'⬜',Free:'⬜'};

// ─── CARD IMAGE ───────────────────────────────────────────────────────────────
function CardImg({card,style}){
  var src=(card.images&&card.images.large)?card.images.large:(card.images&&card.images.small)?card.images.small:null;
  if(src)return<img src={src} alt={card.name} style={style} onError={function(e){e.target.style.display='none';}}/>;
  return<div style={Object.assign({},style,{background:'rgba(255,255,255,.06)',borderRadius:7,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:2,padding:4})}><span style={{fontSize:16}}>🃏</span><span style={{fontSize:7,color:'#555',textAlign:'center',lineHeight:1.2}}>{card.name}</span></div>;
}

// ─── FIELD POKEMON CARD ───────────────────────────────────────────────────────
function FieldCard({poke,small,onTap,onDoubleTap,dimmed}){
  var hp=Math.max(0,poke.currentHP/poke.maxHP);
  var hc=hp>0.5?'#22c55e':hp>0.25?'#f97316':'#ef4444';
  var w=small?48:74,h=small?67:103;
  var tapTimer=useRef(null);
  function handleClick(){
    if(tapTimer.current){clearTimeout(tapTimer.current);tapTimer.current=null;if(onDoubleTap)onDoubleTap(poke.card);return;}
    tapTimer.current=setTimeout(function(){tapTimer.current=null;if(onTap)onTap();},260);
  }
  return(
    <div onClick={handleClick} style={{background:dimmed?'rgba(255,255,255,.02)':'rgba(255,255,255,.06)',border:'1px solid rgba(255,255,255,.12)',borderRadius:10,padding:small?4:7,display:'flex',flexDirection:'column',alignItems:'center',gap:2,minWidth:w+8,cursor:'pointer',opacity:dimmed?.5:1,transition:'all .15s',userSelect:'none'}}>
      <CardImg card={poke.card} style={{width:w,height:h,borderRadius:5,objectFit:'cover',display:'block'}}/>
      <div style={{fontSize:small?7:9,fontWeight:700,color:'#e0d8cc',textAlign:'center',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:w+4}}>{poke.card.name}</div>
      <div style={{fontSize:7,color:'#666'}}>{poke.currentHP}/{poke.maxHP}HP</div>
      <div style={{background:'rgba(255,255,255,.1)',borderRadius:2,height:3,width:'100%',overflow:'hidden'}}>
        <div style={{background:hc,width:(hp*100)+'%',height:'100%',borderRadius:2,transition:'width .3s'}}/>
      </div>
      {poke.energy&&poke.energy.length>0&&<div style={{fontSize:7,color:'#facc15'}}>⚡×{poke.energy.length}</div>}
      {poke.tool&&<div style={{fontSize:6,color:'#a855f7',background:'rgba(168,85,247,.15)',border:'1px solid rgba(168,85,247,.3)',borderRadius:2,padding:'1px 3px',textAlign:'center',maxWidth:w+4,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>🔧 {poke.tool.name}</div>}
    </div>
  );
}

// ─── HAND CARD (draggable) ────────────────────────────────────────────────────
function HandCard({card,index,highlighted,onTap,onDoubleTap,onDragStart}){
  var bc=isPokemon(card)?'rgba(34,197,94,.18)':isEnergy(card)?'rgba(250,204,21,.18)':isStadium(card)?'rgba(16,185,129,.18)':isSupporter(card)?'rgba(168,85,247,.18)':isTool(card)?'rgba(168,85,247,.18)':'rgba(59,130,246,.18)';
  var bc2=isPokemon(card)?'#22c55e':isEnergy(card)?'#facc15':isStadium(card)?'#10b981':isSupporter(card)?'#a855f7':isTool(card)?'#a855f7':'#3b82f6';
  var tapTimer=useRef(null);
  function handleClick(e){
    e.stopPropagation();
    if(tapTimer.current){clearTimeout(tapTimer.current);tapTimer.current=null;if(onDoubleTap)onDoubleTap(card);return;}
    tapTimer.current=setTimeout(function(){tapTimer.current=null;if(onTap)onTap(index);},260);
  }
  function handleDragStart(e){
    e.dataTransfer.setData('cardIndex',String(index));
    e.dataTransfer.effectAllowed='move';
    if(onDragStart)onDragStart(index);
  }
  return(
    <div
      draggable
      onClick={handleClick}
      onDragStart={handleDragStart}
      style={{background:highlighted?bc:'transparent',border:highlighted?'2px solid '+bc2:'2px solid rgba(255,255,255,.08)',borderRadius:8,padding:3,cursor:'grab',transition:'all .15s',flexShrink:0,transform:highlighted?'translateY(-10px) scale(1.06)':'none',userSelect:'none'}}>
      <CardImg card={card} style={{width:52,height:73,borderRadius:5,objectFit:'cover',display:'block'}}/>
      <div style={{fontSize:7,color:'#555',marginTop:2,textAlign:'center',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:56}}>{card.name}</div>
      {highlighted&&<div style={{fontSize:7,color:bc2,textAlign:'center',marginTop:1}}>drag to play</div>}
    </div>
  );
}

// ─── DROP ZONE ────────────────────────────────────────────────────────────────
function DropZone({label,icon,accepts,onDrop,children,glow,dimmed,small}){
  var [over,setOver]=useState(false);
  function handleDragOver(e){e.preventDefault();e.dataTransfer.dropEffect='move';setOver(true);}
  function handleDragLeave(){setOver(false);}
  function handleDrop(e){e.preventDefault();setOver(false);var idx=e.dataTransfer.getData('cardIndex');if(idx!==''&&onDrop)onDrop(parseInt(idx));}
  var borderColor=over?'#facc15':glow?glow:'rgba(255,255,255,.1)';
  var bg=over?'rgba(250,204,21,.08)':glow?glow+'18':'rgba(255,255,255,.03)';
  return(
    <div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
      style={{border:'2px dashed '+borderColor,borderRadius:12,background:bg,
        minWidth:small?58:90,minHeight:small?80:126,display:'flex',flexDirection:'column',
        alignItems:'center',justifyContent:children?'flex-start':'center',
        padding:4,gap:3,transition:'all .2s',opacity:dimmed?.4:1,position:'relative'}}>
      {!children&&<>
        <span style={{fontSize:small?14:20,opacity:.4}}>{icon||'+'}</span>
        <div style={{fontSize:small?7:9,color:'rgba(255,255,255,.25)',textAlign:'center',lineHeight:1.3,padding:'0 4px'}}>{label}</div>
      </>}
      {children}
      {over&&<div style={{position:'absolute',inset:0,border:'2px solid #facc15',borderRadius:11,pointerEvents:'none',boxShadow:'0 0 12px rgba(250,204,21,.4)'}}/>}
    </div>
  );
}

// ─── ZOOM MODAL ───────────────────────────────────────────────────────────────
function ZoomModal({card,onClose}){
  if(!card)return null;
  var isPoke = card.supertype==='Pokemon'||card.supertype==='Pokémon';
  var isTrain= card.supertype==='Trainer';
  var isEn   = card.supertype==='Energy';
  var tc = isPoke ? (TC[card.types&&card.types[0]]||'#facc15') : isTrain ? '#3b82f6' : '#facc15';
  var src=(card.images&&card.images.large)||(card.images&&card.images.small)||null;
  var borderColor = isTrain ? 'rgba(59,130,246,.4)' : isEn ? 'rgba(250,204,21,.4)' : tc+'55';
  return(
    <div onClick={onClose} style={{position:'fixed',inset:0,zIndex:1000,background:'rgba(0,0,0,.88)',backdropFilter:'blur(10px)',display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div onClick={function(e){e.stopPropagation();}} style={{background:'linear-gradient(145deg,#0f1020,#1a1a2e)',border:'1px solid '+borderColor,borderRadius:20,maxWidth:460,width:'100%',maxHeight:'92vh',overflowY:'auto',boxShadow:'0 0 50px '+borderColor}}>
        <div style={{padding:'20px 20px 0',textAlign:'center'}}>
          {src?<img src={src} alt={card.name} style={{maxWidth:'82%',maxHeight:290,borderRadius:14,objectFit:'contain'}}/>:
          <div style={{height:200,background:'rgba(255,255,255,.04)',borderRadius:14,display:'flex',alignItems:'center',justifyContent:'center',fontSize:40}}>🃏</div>}
        </div>
        <div style={{padding:'14px 18px 18px'}}>

          {/* Name + type badges + HP (Pokemon only) */}
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:10}}>
            <div>
              <h2 style={{margin:'0 0 6px',fontSize:20,fontWeight:900,color:'#fff'}}>{card.name}</h2>
              <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                {/* Type badges — only for Pokemon */}
                {isPoke&&(card.types||[]).map(function(t){return<span key={t} style={{padding:'2px 8px',borderRadius:20,fontSize:10,fontWeight:700,background:(TC[t]||'#888')+'22',border:'1px solid '+(TC[t]||'#888')+'55',color:TC[t]||'#888'}}>{t}</span>;})}
                {/* Supertype badge */}
                <span style={{padding:'2px 8px',borderRadius:20,fontSize:10,
                  background:isTrain?'rgba(59,130,246,.15)':isEn?'rgba(250,204,21,.15)':'rgba(255,255,255,.07)',
                  border:isTrain?'1px solid rgba(59,130,246,.4)':isEn?'1px solid rgba(250,204,21,.4)':'1px solid rgba(255,255,255,.12)',
                  color:isTrain?'#3b82f6':isEn?'#facc15':'#888'}}>
                  {card.supertype}{card.subtypes&&card.subtypes.length?' · '+card.subtypes.join(', '):''}
                </span>
              </div>
            </div>
            {/* HP — Pokemon only */}
            {isPoke&&card.hp&&<div style={{textAlign:'right',flexShrink:0,marginLeft:10}}><div style={{fontSize:30,fontWeight:900,color:tc,lineHeight:1}}>{card.hp}</div><div style={{fontSize:9,color:'#555'}}>HP</div></div>}
          </div>

          {/* Abilities — Pokemon only */}
          {isPoke&&(card.abilities||[]).map(function(ab,i){return(<div key={i} style={{background:'rgba(168,85,247,.08)',border:'1px solid rgba(168,85,247,.3)',borderRadius:10,padding:10,marginBottom:7}}><div style={{fontSize:9,color:'#a855f7',fontWeight:800,letterSpacing:1.5,marginBottom:3}}>{(ab.type||'ABILITY').toUpperCase()}</div><div style={{fontWeight:800,fontSize:13,marginBottom:3}}>{ab.name}</div>{ab.text&&<div style={{fontSize:11,color:'#bbb',lineHeight:1.55}}>{ab.text}</div>}</div>);})}

          {/* Attacks — Pokemon only */}
          {isPoke&&(card.attacks||[]).map(function(atk,i){return(<div key={i} style={{background:'rgba(255,255,255,.04)',border:'1px solid rgba(255,255,255,.09)',borderRadius:10,padding:10,marginBottom:7}}><div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}><div style={{display:'flex',alignItems:'center',gap:6}}><div style={{display:'flex',gap:2}}>{(atk.cost||[]).map(function(c,j){return<span key={j} style={{fontSize:13}}>{EE[c]||'⬜'}</span>;})}</div><span style={{fontWeight:800,fontSize:13}}>{atk.name}</span></div>{atk.damage&&<span style={{fontWeight:900,fontSize:16,color:tc}}>{atk.damage}</span>}</div>{atk.text&&<div style={{fontSize:11,color:'#bbb',lineHeight:1.55}}>{atk.text}</div>}</div>);})}

          {/* Rules text — Trainers and Energy */}
          {(card.rules||[]).map(function(r,i){return<div key={i} style={{background:isTrain?'rgba(59,130,246,.06)':'rgba(250,204,21,.05)',border:isTrain?'1px solid rgba(59,130,246,.2)':'1px solid rgba(250,204,21,.15)',borderRadius:10,padding:12,marginBottom:7}}><div style={{fontSize:12,color:isTrain?'#93c5fd':'#d4c89a',lineHeight:1.65}}>{r}</div></div>;})}

          {/* Weakness / Resistance / Retreat — Pokemon only */}
          {isPoke&&<div style={{display:'flex',gap:6,flexWrap:'wrap',marginTop:6,marginBottom:10}}>
            {(card.weaknesses||[]).map(function(w,i){return<div key={i} style={{padding:'3px 10px',borderRadius:7,fontSize:11,background:'rgba(239,68,68,.09)',border:'1px solid rgba(239,68,68,.3)'}}><span style={{color:'#ef4444',fontSize:9,fontWeight:800}}>WEAK </span>{w.type} {w.value}</div>;})}
            {(card.resistances||[]).map(function(r,i){return<div key={i} style={{padding:'3px 10px',borderRadius:7,fontSize:11,background:'rgba(34,197,94,.09)',border:'1px solid rgba(34,197,94,.3)'}}><span style={{color:'#22c55e',fontSize:9,fontWeight:800}}>RESIST </span>{r.type} {r.value}</div>;})}
            {card.retreatCost&&card.retreatCost.length>0&&<div style={{padding:'3px 10px',borderRadius:7,fontSize:11,background:'rgba(255,255,255,.05)',border:'1px solid rgba(255,255,255,.1)'}}>⬜ Retreat ×{card.retreatCost.length}</div>}
          </div>}

          {/* Footer */}
          {(card.set||card.rarity)&&<div style={{paddingTop:8,borderTop:'1px solid rgba(255,255,255,.07)',display:'flex',justifyContent:'space-between',fontSize:10,color:'#555',marginBottom:5}}><span>{card.set&&card.set.name?card.set.name:''}</span><span>{card.number?'#'+card.number:''}{card.rarity?' · '+card.rarity:''}</span></div>}
          {card.artist&&<div style={{fontSize:10,color:'#444',textAlign:'center',marginBottom:10}}>✏️ {card.artist}</div>}
          <button onClick={onClose} style={{width:'100%',padding:'10px',background:'rgba(250,204,21,.08)',border:'1px solid rgba(250,204,21,.3)',borderRadius:10,color:'#facc15',cursor:'pointer',fontSize:13,fontWeight:700}}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN BATTLE ──────────────────────────────────────────────────────────────
export default function Battle({deck,onExit}){
  var [enrichedDeck,setEnriched] = useState(null);
  var [enriching,setEnriching]   = useState(true);
  var [gs,setGs]                 = useState(null);
  var [phase,setPhase]           = useState('loading');
  var [log,setLog]               = useState([]);
  var [winner,setWinner]         = useState(null);
  var [highlighted,setHL]        = useState(null); // hand card index
  var [zoomCard,setZoom]         = useState(null);
  var [msg,setMsg]               = useState('');
  var [rcOpts,setRCOpts]         = useState(null);
  var [rcCard,setRCCard]         = useState(null);
  var [confirm,setConfirm]       = useState(null); // {msg, onYes, onNo}
  var [history,setHistory]       = useState([]); // snapshots for rewind
  var [historyIdx,setHistoryIdx] = useState(-1); // current rewind position
  var logRef=useRef(null);

  useEffect(function(){if(logRef.current)logRef.current.scrollIntoView({behavior:'smooth'});},[log]);

  // Save a snapshot for rewind (called after each meaningful action)
  function saveSnapshot(g, logEntry){
    var snap={gs:clone(g), logEntry:logEntry, time:Date.now()};
    setHistory(function(h){ return h.slice(0,50).concat([snap]); }); // keep last 50
    setHistoryIdx(-1); // reset rewind position
  }

  useEffect(function(){
    setEnriching(true);
    enrichDeckCards(deck.cards).then(function(enriched){
      setEnriched(Object.assign({},deck,{cards:enriched}));
      setEnriching(false);
    });
  },[deck]);

  useEffect(function(){if(!enrichedDeck)return;startGame(enrichedDeck);},[enrichedDeck]);

  function startGame(d){
    var player=buildState(d.cards,'You');
    var ai=buildState(d.cards,'AI');
    ai=aiSetup(ai);
    setGs({player:player,ai:ai,turn:1});
    setPhase('pick_active');
    setLog(['⚔️ Battle! Drag a Basic Pokémon from hand to the Active slot to begin.','AI active: '+(ai.active?ai.active.card.name:'none')]);
    setWinner(null);setHL(null);setMsg('');setRCOpts(null);setRCCard(null);
  }

  function addLog(e){setLog(function(p){return p.concat(Array.isArray(e)?e:[e]);});}

  function checkWin(g){
    if(g.player.prizes.length===0)return'player';
    if(g.ai.prizes.length===0)return'ai';
    if(!g.player.active&&g.player.bench.length===0)return'ai';
    if(!g.ai.active&&g.ai.bench.length===0)return'player';
    return null;
  }

  // ── Drop handler — the core play mechanic ─────────────────────────────────
  function handleDrop(zone,cardIdx){
    var g=clone(gs);
    var card=g.player.hand[cardIdx];
    if(!card){setMsg('Invalid card.');return;}

    // ── ACTIVE ZONE ──────────────────────────────────────────────────────────
    if(zone==='active'){
      if(phase==='pick_active'){
        if(!isBasic(card)){setMsg('Drag a Basic Pokémon to the Active slot!');return;}
        g.player.hand.splice(cardIdx,1);g.player.active=makeInPlay(card);
        setPhase('auto_draw');addLog(['You placed '+card.name+' as Active. Game begins!']);setGs(g);setHL(null);return;
      }
      if(phase!=='player_turn'){return;}
      if(isBasic(card)){
        if(!g.player.active){g.player.hand.splice(cardIdx,1);g.player.active=makeInPlay(card);addLog(['You placed '+card.name+' as Active.']);setGs(g);setHL(null);return;}
        setMsg('Already have an active. Drag to bench instead.');return;
      }
      if((isStage1(card)||isStage2(card))&&g.player.active){
        if(card.evolvesFrom&&g.player.active.card.name.toLowerCase()===card.evolvesFrom.toLowerCase()){
          // Block evolution on turn 1
          if(g.turn<=1){setMsg('Cannot evolve on the first turn.');return;}
          // Confirm before evolving
          var evoCard=card,evoIdx=cardIdx,evoGs=g;
          setConfirm({
            msg:'Evolve '+card.evolvesFrom+' → '+card.name+'?',
            onYes:function(){
              var g2=clone(evoGs);
              g2.player.hand.splice(evoIdx,1);
              g2.player.active={card:evoCard,currentHP:getHP(evoCard),maxHP:getHP(evoCard),energy:clone(g2.player.active.energy),statusCondition:null};
              addLog(['You evolved '+evoCard.evolvesFrom+' → '+evoCard.name+'!']);setGs(g2);setHL(null);setConfirm(null);
            },
            onNo:function(){setConfirm(null);}
          });
          return;
        }
        setMsg(card.name+' evolves from '+card.evolvesFrom+'. That\'s not your active.');return;
      }
      if(isEnergy(card)){
        if(g.player.hasAttachedEnergy){setMsg('Only 1 energy per turn.');return;}
        g.player.hand.splice(cardIdx,1);g.player.active.energy.push(card);g.player.hasAttachedEnergy=true;
        addLog(['You attached '+card.name+' to '+g.player.active.card.name+'.']);setGs(g);setHL(null);return;
      }
      setMsg('Cannot drag that to the Active zone.');return;
    }

    // ── BENCH ZONES ───────────────────────────────────────────────────────────
    if(zone==='bench'||zone.startsWith('bench-')){
      if(phase!=='player_turn'&&phase!=='pick_active'){return;}
      var benchIdx=zone.startsWith('bench-')?parseInt(zone.split('-')[1]):-1;
      if(isBasic(card)){
        if(isBenchFull(g.player.bench)){setMsg('Bench is full! (max 5)');return;}
        g.player.hand.splice(cardIdx,1);g.player.bench.push(makeInPlay(card));
        addLog(['You benched '+card.name+'.']);setGs(g);setHL(null);return;
      }
      if((isStage1(card)||isStage2(card))&&benchIdx>=0&&g.player.bench[benchIdx]){
        var target=g.player.bench[benchIdx];
        if(card.evolvesFrom&&target.card.name.toLowerCase()===card.evolvesFrom.toLowerCase()){
          // Block evolution on turn 1
          if(g.turn<=1){setMsg('Cannot evolve on the first turn.');return;}
          // Confirm before evolving
          var bEvoCard=card,bEvoIdx=cardIdx,bEvoTarget=benchIdx,bEvoGs=g;
          setConfirm({
            msg:'Evolve benched '+card.evolvesFrom+' → '+card.name+'?',
            onYes:function(){
              var g2=clone(bEvoGs);
              g2.player.hand.splice(bEvoIdx,1);
              var bt=g2.player.bench[bEvoTarget];
              g2.player.bench[bEvoTarget]={card:bEvoCard,currentHP:getHP(bEvoCard),maxHP:getHP(bEvoCard),energy:clone(bt.energy),statusCondition:null};
              addLog(['You evolved benched '+bEvoCard.evolvesFrom+' → '+bEvoCard.name+'!']);setGs(g2);setHL(null);setConfirm(null);
            },
            onNo:function(){setConfirm(null);}
          });
          return;
        }
        setMsg(card.name+' evolves from '+card.evolvesFrom+'.');return;
      }
      if(isEnergy(card)&&benchIdx>=0&&g.player.bench[benchIdx]){
        if(g.player.hasAttachedEnergy){setMsg('Only 1 energy per turn.');return;}
        g.player.hand.splice(cardIdx,1);g.player.bench[benchIdx].energy.push(card);g.player.hasAttachedEnergy=true;
        addLog(['You attached '+card.name+' to benched '+g.player.bench[benchIdx].card.name+'.']);setGs(g);setHL(null);return;
      }
      setMsg('Cannot play that there.');return;
    }

    // ── TOOL ZONES ────────────────────────────────────────────────────────────
    if(zone==='tool-active'||zone.indexOf('tool-bench-')===0){
      if(!isTool(card)){setMsg("Only Pokémon Tool cards can be attached here.");return;}
      if(g.player.hasPlayedTool){setMsg('Already played a Tool this turn.');return;}
      var isActiveTool=zone==='tool-active';
      var toolTarget2;
      if(isActiveTool){
        if(!g.player.active){setMsg('No Active Pokémon to attach tool to.');return;}
        if(g.player.active.tool){setMsg(g.player.active.card.name+' already has a Tool.');return;}
        toolTarget2=g.player.active.card.name;
      } else {
        var bIdx3=parseInt(zone.replace('tool-bench-',''));
        if(!g.player.bench[bIdx3]){setMsg('No Pokémon in that slot.');return;}
        if(g.player.bench[bIdx3].tool){setMsg(g.player.bench[bIdx3].card.name+' already has a Tool.');return;}
        toolTarget2=g.player.bench[bIdx3].card.name;
      }
      var tCardZ=card,tIdxZ=cardIdx,tGsZ=g,tZoneZ=zone,tTargZ=toolTarget2;
      setConfirm({
        msg:'Attach '+card.name+' to '+toolTarget2+'?',
        onYes:function(){
          var g2=clone(tGsZ);
          g2.player.hand.splice(tIdxZ,1);
          if(tZoneZ==='tool-active'){g2.player.active.tool=tCardZ;}
          else{var bI2=parseInt(tZoneZ.replace('tool-bench-',''));g2.player.bench[bI2].tool=tCardZ;}
          g2.player.hasPlayedTool=true;
          addLog(['Attached '+tCardZ.name+' to '+tTargZ+'!']);
          setGs(g2);setHL(null);setConfirm(null);
        },
        onNo:function(){setConfirm(null);}
      });
      return;
    }

    // ── TRAINER ZONE ──────────────────────────────────────────────────────────
    if(zone==='trainer'){
      if(phase!=='player_turn'){setMsg('Not your turn.');return;}
      if(!isItem(card)){setMsg('Only Item cards go in the Trainer zone. Drag Supporters to the Supporter slot, Stadiums to the Stadium slot.');return;}
      if(card.name.toLowerCase().includes('rare candy')){
        g.player.hand.splice(cardIdx,1);// hand splice before calling
        var result=applyTrainer(card,g,null);
        if(!result.ok){// re-add to hand
          g.player.hand.splice(cardIdx,0,card);setMsg(result.msg||'Cannot play that.');return;}
        if(result.needsTarget){setRCOpts(result.needsTarget.options);setRCCard({card:card,idx:cardIdx});setGs(g);setHL(null);return;}
        addLog(result.log);setGs(result.gs);setHL(null);return;
      }
      // Confirm before playing item trainer
      var tCard=card,tIdx=cardIdx,tGs=g;
      setConfirm({
        msg:'Play '+card.name+'?',
        onYes:function(){
          var r2=applyTrainer(tCard,tGs,null);
          if(!r2.ok){setMsg(r2.msg||'Cannot play that.');setConfirm(null);return;}
          if(isItem(tCard))tGs.player.trainerSlot=tCard;
          addLog(r2.log);setGs(r2.gs);setHL(null);setConfirm(null);
        },
        onNo:function(){setConfirm(null);}
      });
      return;
    }

    // ── SUPPORTER ZONE ────────────────────────────────────────────────────────
    if(zone==='supporter'){
      if(phase!=='player_turn'){setMsg('Not your turn.');return;}
      if(!isSupporter(card)){setMsg('Only Supporter cards go here.');return;}
      if(g.player.hasPlayedSupporter){setMsg('Only 1 Supporter per turn.');return;}
      // Confirm before playing supporter
      var sCard=card,sIdx=cardIdx,sGs=g;
      setConfirm({
        msg:'Play supporter '+card.name+'?',
        onYes:function(){
          var r3=applyTrainer(sCard,sGs,null);
          if(!r3.ok){setMsg(r3.msg||'Cannot play that.');setConfirm(null);return;}
          sGs.player.hasPlayedSupporter=true;
          addLog(r3.log);setGs(r3.gs);setHL(null);setConfirm(null);
        },
        onNo:function(){setConfirm(null);}
      });
      return;
    }

    // ── STADIUM ZONE ─────────────────────────────────────────────────────────
    if(zone==='stadium'){
      if(phase!=='player_turn'){setMsg('Not your turn.');return;}
      if(!isStadium(card)){setMsg('Only Stadium cards go here.');return;}
      g.player.hand.splice(cardIdx,1);
      if(g.player.stadium)g.player.discard.push(g.player.stadium);
      g.player.stadium=card;
      addLog(['You played '+card.name+' as the Stadium.']);setGs(g);setHL(null);return;
    }

    // ── DISCARD ───────────────────────────────────────────────────────────────
    if(zone==='discard'){
      if(phase!=='player_turn')return;
      g.player.hand.splice(cardIdx,1);g.player.discard.push(card);
      addLog(['You discarded '+card.name+'.']);setGs(g);setHL(null);return;
    }
  }

  // ── Rare Candy confirm ───────────────────────────────────────────────────
  function confirmRC(opt){
    var g=clone(gs);
    var result=applyTrainer(rcCard.card,g,opt);
    if(!result.ok){setMsg(result.msg);setRCOpts(null);setRCCard(null);return;}
    addLog(result.log);setRCOpts(null);setRCCard(null);setHL(null);setGs(result.gs);
  }

  // ── Attack ───────────────────────────────────────────────────────────────
  function doAttack(atkIdx){
    if(phase!=='player_turn')return;
    var g=clone(gs);
    if(!g.player.active||!g.ai.active){setMsg('Need both actives to attack!');return;}
    var atk=g.player.active.card.attacks&&g.player.active.card.attacks[atkIdx];
    if(!atk)return;
    if(!canAtk(atk,countE(g.player.active.energy))){setMsg('Not enough energy!');return;}
    var dmg=calcDmg(atk,g.player.active,g.ai.active);
    g.ai.active.currentHP-=dmg;
    var nl=['⚡ '+atk.name+'!'+(dmg>0?' '+dmg+' damage to '+g.ai.active.card.name+'.':' No damage.')];
    if(g.ai.active.currentHP<=0){
      nl.push('💀 '+g.ai.active.card.name+' KO\'d!');
      g.ai.discard.push(g.ai.active.card);g.ai.active=null;
      if(g.player.prizes.length>0){var p=g.player.prizes.splice(0,1)[0];g.player.hand.push(p);nl.push('🏆 Prize taken! ('+g.player.prizes.length+' left)');}
      if(g.ai.bench.length>0){g.ai.active=g.ai.bench.shift();nl.push('AI sent out '+g.ai.active.card.name+'.');}
    }
    var atkEntry='⚡ Turn '+g.turn+': '+atk.name+(dmg>0?' ('+dmg+'dmg)':'');
    saveSnapshot(g, atkEntry);
    addLog(nl);
    var w=checkWin(g);if(w){setWinner(w);setPhase('game_over');setGs(g);return;}
    g.player.hasDrawn=false;g.player.hasAttachedEnergy=false;g.player.hasRetreated=false;g.player.hasPlayedSupporter=false;g.player.hasPlayedTool=false;g.player.trainerSlot=null;g.turn=g.turn+1;
    setGs(g);setPhase('ai_turn');setMsg('');
  }

  function doRetreat(benchIdx){
    if(phase!=='player_turn')return;
    var g=clone(gs);
    if(!g.player.active){setMsg('No active!');return;}
    if(g.player.hasRetreated){setMsg('Already retreated.');return;}
    var cost=g.player.active.card.retreatCost?g.player.active.card.retreatCost.length:0;
    if(countE(g.player.active.energy)<cost){setMsg('Need '+cost+' energy to retreat.');return;}
    for(var i=0;i<cost;i++)g.player.active.energy.splice(0,1);
    var old=g.player.active;g.player.active=g.player.bench.splice(benchIdx,1)[0];g.player.bench.push(old);
    g.player.hasRetreated=true;addLog(['Retreated '+old.card.name+' → '+g.player.active.card.name+'.']);setGs(g);setMsg('');
  }

  function doDraw(){
    if(phase!=='player_turn')return;
    var g=clone(gs);
    if(g.player.hasDrawn){setMsg('Already drew this turn. Play a Trainer to draw more.');return;}
    if(!g.player.deck.length){addLog(['Deck empty — you lose!']);setWinner('ai');setPhase('game_over');return;}
    var c=g.player.deck.splice(0,1)[0];g.player.hand.push(c);
    g.player.hasDrawn=true;
    addLog(['Drew '+c.name+'.']);setGs(g);setMsg('');
  }

  // ── AI turn ──────────────────────────────────────────────────────────────
  useEffect(function(){
    if(phase!=='ai_turn'||!gs)return;
    var t=setTimeout(function(){
      var g=clone(gs);var nl=['— AI Turn —'];
      if(!g.ai.deck.length){nl.push('AI deck empty — you win!');addLog(nl);setWinner('player');setPhase('game_over');return;}
      var d=g.ai.deck.splice(0,1)[0];g.ai.hand.push(d);nl.push('AI drew.');
      var r=aiTurn(g.ai);g.ai=r.ai;nl=nl.concat(r.log);
      if(g.ai.active&&g.player.active){
        var atk=aiAtk(g.ai,g.player);
        if(atk){
          var dmg=calcDmg(atk,g.ai.active,g.player.active);g.player.active.currentHP-=dmg;
          nl.push('🤖 AI: '+atk.name+(dmg>0?' ('+dmg+' dmg)':''));
          if(g.player.active.currentHP<=0){
            nl.push('💀 Your '+g.player.active.card.name+' KO\'d!');
            g.player.discard.push(g.player.active.card);g.player.active=null;
            if(g.ai.prizes.length>0){var p=g.ai.prizes.splice(0,1)[0];g.ai.hand.push(p);nl.push('AI took prize! ('+g.ai.prizes.length+' left)');}
            if(g.player.bench.length>0){g.player.active=g.player.bench.shift();nl.push('Your '+g.player.active.card.name+' promoted.');}
          }
        } else nl.push('AI building energy...');
      }
      g.ai.hasAttachedEnergy=false;g.ai.hasRetreated=false;g.ai.hasPlayedSupporter=false;
      // Reset ALL player flags at the start of their new turn
      g.player.hasDrawn=false;g.player.hasAttachedEnergy=false;g.player.hasRetreated=false;g.player.hasPlayedSupporter=false;g.player.hasPlayedTool=false;g.player.trainerSlot=null;
      addLog(nl);var w=checkWin(g);if(w){setWinner(w);setPhase('game_over');setGs(g);return;}
      setGs(g);setPhase('auto_draw'); // triggers auto-draw at start of player turn
    },1500);
    return function(){clearTimeout(t);};
  },[phase]);

  // ── Auto-draw at start of player turn ───────────────────────────────────
  useEffect(function(){
    if(phase!=='auto_draw'||!gs)return;
    var t=setTimeout(function(){
      var g=clone(gs);
      if(!g.player.deck.length){
        addLog(['Your deck is empty — you lose!']);setWinner('ai');setPhase('game_over');return;
      }
      var c=g.player.deck.splice(0,1)[0];
      g.player.hand.push(c);
      g.player.hasDrawn=true;
      var entry='📥 Auto-drew: '+c.name+'.';
      addLog([entry]);
      saveSnapshot(g, entry);
      setGs(g);setPhase('player_turn');
    },400);
    return function(){clearTimeout(t);};
  },[phase]);

  // ── RENDER ───────────────────────────────────────────────────────────────
  if(enriching)return(
    <div style={S.root}>
      <div style={S.hdr}><span style={{color:'#facc15',fontWeight:900}}>⚡ {deck.name}</span><button onClick={onExit} style={S.bG}>Exit</button></div>
      <div style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:12}}>
        <div style={{fontSize:36,animation:'spin 1.1s linear infinite',display:'inline-block'}}>⚡</div>
        <div style={{color:'#888',fontSize:15}}>Loading card data...</div>
        <div style={{color:'#555',fontSize:12}}>Fetching correct era printings from API</div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if(!gs)return<div style={S.root}><div style={{padding:60,textAlign:'center',color:'#555'}}>Setting up...</div></div>;

  var player=gs.player,ai=gs.ai;

  return(
    <div style={S.root} onClick={function(){setHL(null);}}>

      {/* HEADER */}
      <div style={S.hdr}>
        <span style={{color:'#facc15',fontWeight:900,fontSize:15}}>⚔️ {deck.name}</span>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <span style={{fontSize:10,fontWeight:700,padding:'3px 8px',borderRadius:6,
            background:phase==='player_turn'?'rgba(34,197,94,.15)':phase==='ai_turn'?'rgba(249,115,22,.15)':phase==='pick_active'?'rgba(59,130,246,.15)':'rgba(239,68,68,.15)',
            color:phase==='player_turn'?'#22c55e':phase==='ai_turn'?'#f97316':phase==='pick_active'?'#3b82f6':'#ef4444',border:'1px solid currentColor'}}>
            {phase==='player_turn'?'YOUR TURN':phase==='ai_turn'?'AI THINKING':phase==='pick_active'?'PLACE ACTIVE':'GAME OVER'}
          </span>
          <span style={{fontSize:11,color:'#555'}}>Turn {gs.turn}</span>
          <button onClick={function(){startGame(enrichedDeck);}} style={S.bY}>↺</button>
          <button onClick={onExit} style={S.bG}>Exit</button>
        </div>
      </div>

      {/* GAME OVER */}
      {phase==='game_over'&&(
        <div style={{background:winner==='player'?'rgba(34,197,94,.12)':'rgba(239,68,68,.12)',border:'1px solid '+(winner==='player'?'#22c55e':'#ef4444'),borderRadius:12,margin:'8px 12px',padding:'12px 16px',textAlign:'center'}}>
          <div style={{fontSize:24,fontWeight:900,color:winner==='player'?'#22c55e':'#ef4444',marginBottom:6}}>{winner==='player'?'🏆 You Win!':'💀 You Lose!'}</div>
          <div style={{display:'flex',gap:8,justifyContent:'center'}}>
            <button onClick={function(){startGame(enrichedDeck);}} style={S.bY}>Play Again</button>
            <button onClick={onExit} style={S.bG}>Exit</button>
          </div>
        </div>
      )}

      {/* RARE CANDY */}
      {rcOpts&&(
        <div style={{background:'rgba(250,204,21,.08)',border:'1px solid #facc15',borderRadius:10,margin:'6px 12px',padding:10}}>
          <div style={{fontWeight:700,color:'#facc15',marginBottom:6,fontSize:13}}>🍬 Rare Candy — choose target:</div>
          <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
            {rcOpts.map(function(opt,i){return(<button key={i} onClick={function(){confirmRC(opt);}} style={{padding:'6px 12px',borderRadius:7,background:'rgba(250,204,21,.15)',border:'1px solid #facc15',color:'#facc15',cursor:'pointer',fontSize:11,fontWeight:700}}>{opt.slot.poke.card.name} → {opt.card.name} ({opt.slot.loc})</button>);})}
            <button onClick={function(){setRCOpts(null);setRCCard(null);}} style={{padding:'6px 12px',borderRadius:7,background:'rgba(255,255,255,.05)',border:'1px solid rgba(255,255,255,.15)',color:'#aaa',cursor:'pointer',fontSize:11}}>Cancel</button>
          </div>
        </div>
      )}

      {/* BODY: play area + log sidebar */}
      <div style={S.body}>
      <div style={S.playCol}>
      {/* GAME MAT */}
      <div style={S.mat}>
        <div style={S.matInner}>

        {/* ── AI FIELD ── */}
        <div style={S.field}>
          <div style={S.fieldLabel}>🤖 AI · {ai.prizes.length} prizes · {ai.hand.length} in hand · {ai.deck.length} in deck</div>
          {/* AI bench — centered above active (mirrored layout) */}
          <div style={{display:'flex',justifyContent:'center',gap:4,flexWrap:'wrap',marginBottom:6,minHeight:36}}>
            {ai.bench.map(function(p,i){return(<FieldCard key={i} poke={p} small onDoubleTap={setZoom}/>);})}
            {ai.bench.length===0&&<span style={{color:'#333',fontSize:10,alignSelf:'center'}}>Empty bench</span>}
          </div>
          {/* AI active + counters row */}
          <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:12}}>
            <div style={{display:'flex',flexDirection:'column',gap:4}}>
              <Counter label="PRIZES" value={ai.prizes.length} color="#facc15"/>
              <Counter label="DECK" value={ai.deck.length} color="#aaa"/>
            </div>
            <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:4}}>
              <div style={{fontSize:9,color:'#444',letterSpacing:1}}>ACTIVE</div>
              {ai.active?<FieldCard poke={ai.active} onDoubleTap={setZoom}/>:<div style={{width:90,height:126,border:'1px dashed rgba(255,255,255,.08)',borderRadius:10,display:'flex',alignItems:'center',justifyContent:'center',color:'#333',fontSize:10}}>No active</div>}
            </div>
          </div>
        </div>

        {/* ── STADIUM ZONE ── */}
        <div style={{display:'flex',justifyContent:'center',alignItems:'center',gap:8,padding:'4px 0'}}>
          <div style={{flex:1,height:1,background:'rgba(255,255,255,.05)'}}/>
          <DropZone zone="stadium" label="STADIUM" icon="🏟️" onDrop={function(i){handleDrop('stadium',i);}} glow="#10b981" small>
            {gs.player.stadium&&<div style={{padding:4,textAlign:'center'}}><div style={{fontSize:9,color:'#10b981',fontWeight:700,marginBottom:2}}>{gs.player.stadium.name}</div><div style={{fontSize:8,color:'#555'}}>Stadium</div></div>}
          </DropZone>
          <div style={{flex:1,height:1,background:'rgba(255,255,255,.05)'}}/>
        </div>

        {/* ── PLAYER FIELD ── */}
        <div style={S.field}>
          {/* Player active + counters — centered */}
          <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:12,marginBottom:6}}>
            <div style={{display:'flex',flexDirection:'column',gap:4}}>
              <Counter label="PRIZES" value={player.prizes.length} color="#facc15"/>
              <Counter label="DECK" value={player.deck.length} color="#aaa"/>
              <Counter label="DISC" value={player.discard.length} color="#555"/>
            </div>
            <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:4}}>
              <div style={{fontSize:9,color:'#555',letterSpacing:1}}>ACTIVE</div>
              <DropZone zone="active" label={phase==='pick_active'?'Drag Basic here':'Drop to play/evolve/attach'} icon="⭐" onDrop={function(i){handleDrop('active',i);}}>
                {player.active&&<FieldCard poke={player.active} onDoubleTap={setZoom}/>}
              </DropZone>
            </div>
          </div>
          {/* Player bench — centered below active */}
          <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:4}}>
            <div style={{fontSize:9,color:'#555',letterSpacing:1}}>BENCH ({player.bench.length}/5)</div>
            <div style={{display:'flex',gap:4,flexWrap:'wrap',justifyContent:'center'}}>
              {player.bench.map(function(p,i){
                return(
                  <div key={i} style={{display:'flex',flexDirection:'column',alignItems:'center',gap:2}}>
                    <DropZone zone={'bench-'+i} label="" onDrop={function(ci){handleDrop('bench-'+i,ci);}}>
                      <FieldCard poke={p} small onDoubleTap={setZoom} onTap={function(){doRetreat(i);}}/>
                      <div style={{fontSize:7,color:'#555',textAlign:'center'}}>tap retreat</div>
                    </DropZone>
                    <DropZone zone={'tool-bench-'+i} label="TOOL" icon="🔧"
                      onDrop={function(ci){handleDrop('tool-bench-'+i,ci);}}
                      glow={!p.tool&&!player.hasPlayedTool?'#a855f7':null}
                      dimmed={!!p.tool||player.hasPlayedTool}
                      small>
                      {p.tool&&<div style={{fontSize:7,color:'#a855f7',fontWeight:700,lineHeight:1.2,padding:'1px 2px',textAlign:'center'}}>{p.tool.name}</div>}
                    </DropZone>
                  </div>
                );
              })}
              {player.bench.length<5&&(
                <DropZone zone="bench" label="Bench" icon="🔵" onDrop={function(i){handleDrop('bench',i);}} small/>
              )}
            </div>
          </div>

          {/* Trainer / Supporter / Tool row */}
          <div style={{display:'flex',gap:6,marginTop:8,flexWrap:'wrap',alignItems:'center',justifyContent:'center'}}>
            <DropZone zone="trainer" label="ITEM" icon="🎒" onDrop={function(i){handleDrop('trainer',i);}} glow="#3b82f6" small>
              {gs.player.trainerSlot&&<div style={{padding:3,textAlign:'center'}}><div style={{fontSize:8,color:'#3b82f6',fontWeight:700,lineHeight:1.2}}>{gs.player.trainerSlot.name}</div></div>}
            </DropZone>
            <DropZone zone="supporter" label="SUPPORTER" icon="👤" onDrop={function(i){handleDrop('supporter',i);}} glow={player.hasPlayedSupporter?null:'#a855f7'} dimmed={player.hasPlayedSupporter} small/>
            {/* Tool zone — attach to Active */}
            <DropZone zone="tool-active" label="TOOL" icon="🔧"
              onDrop={function(i){handleDrop('tool-active',i);}}
              glow={player.active&&!player.active.tool&&!player.hasPlayedTool?'#a855f7':null}
              dimmed={!player.active||!!player.active.tool||player.hasPlayedTool}
              small>
              {player.active&&player.active.tool&&(
                <div style={{padding:3,textAlign:'center'}}>
                  <div style={{fontSize:8,color:'#a855f7',fontWeight:700,lineHeight:1.2}}>{player.active.tool.name}</div>
                </div>
              )}
            </DropZone>
            <DropZone zone="discard" label="DISCARD" icon="🗑️" onDrop={function(i){handleDrop('discard',i);}} small/>

            {/* Attacks */}
            {phase==='player_turn'&&player.active&&(player.active.card.attacks||[]).length>0&&(
              <div style={{display:'flex',gap:5,flexWrap:'wrap',justifyContent:'center'}}>
                {(player.active.card.attacks||[]).map(function(atk,i){
                  var ec=countE(player.active.energy),ok=canAtk(atk,ec);
                  return(<button key={i} onClick={function(){doAttack(i);}} disabled={!ok||!ai.active}
                    style={{padding:'6px 10px',borderRadius:7,fontSize:11,fontWeight:700,cursor:ok&&ai.active?'pointer':'default',border:ok?'1px solid #facc15':'1px solid rgba(255,255,255,.1)',background:ok?'rgba(250,204,21,.1)':'rgba(255,255,255,.04)',color:ok?'#facc15':'#555'}}>
                    {atk.name}{atkDmg(atk)>0?' ('+atkDmg(atk)+')':''}
                    {!ok&&<span style={{fontSize:9,marginLeft:3,color:'#555'}}>({ecost(atk.cost)}⚡)</span>}
                  </button>);
                })}
              </div>
            )}
          </div>

          {/* Action buttons */}
          {phase==='player_turn'&&(
            <div style={{display:'flex',gap:6,marginTop:7,flexWrap:'wrap',alignItems:'center',justifyContent:'center'}}>
              <button onClick={doDraw}
                style={Object.assign({},S.bB,gs&&gs.player.hasDrawn?{opacity:0.35,cursor:'default',textDecoration:'line-through'}:{})}>
                Draw{gs&&gs.player.hasDrawn?' ✓':''}
              </button>
              <button onClick={function(){setPhase('ai_turn');addLog(['You ended your turn.']);}} style={S.bG}>End Turn</button>
              {msg&&<div style={{fontSize:11,color:'#f97316',background:'rgba(249,115,22,.08)',border:'1px solid rgba(249,115,22,.25)',borderRadius:6,padding:'4px 10px'}}>⚠️ {msg}</div>}
            </div>
          )}
          {phase==='ai_turn'&&<div style={{marginTop:8,padding:'6px 10px',background:'rgba(255,255,255,.04)',borderRadius:8,fontSize:12,color:'#888',display:'flex',alignItems:'center',gap:6}}><span style={{animation:'spin 1s linear infinite',display:'inline-block'}}>⚡</span> AI is thinking...</div>}
          {phase==='pick_active'&&<div style={{marginTop:8,padding:'6px 10px',background:'rgba(59,130,246,.08)',border:'1px solid rgba(59,130,246,.25)',borderRadius:8,fontSize:12,color:'#3b82f6'}}>👆 Tap to highlight a card · Drag a Basic Pokémon to the Active zone to begin</div>}
          <div style={S.fieldLabel}>👤 You</div>
        </div>
        </div>
      </div>

      {/* HAND */}
      <div style={S.handArea}>
        <div style={{fontSize:9,color:'#444',letterSpacing:1,marginBottom:5,display:'flex',alignItems:'center',gap:6}}>
          HAND ({player.hand.length})
          <span style={{color:'#333',fontSize:9}}>· tap to highlight · drag to play · double-tap to inspect</span>
        </div>
        <div style={S.handRow}>
          {player.hand.map(function(card,i){
            return(
              <HandCard key={i} card={card} index={i} highlighted={highlighted===i}
                onTap={function(idx){setHL(highlighted===idx?null:idx);setMsg('');}}
                onDoubleTap={setZoom}
                onDragStart={function(){setHL(i);}}
              />
            );
          })}
          {player.hand.length===0&&<span style={{color:'#444',fontSize:12,alignSelf:'center'}}>No cards in hand</span>}
        </div>
      </div>

      </div> {/* /playCol */}

      {/* LOG SIDEBAR */}
      <div style={S.logSidebar}>
        <div style={S.logSidebarHdr}>
          <span style={{fontSize:9,color:'#666',fontWeight:700,letterSpacing:'0.08em'}}>BATTLE LOG</span>
          <div style={{display:'flex',gap:4,alignItems:'center'}}>
            {history.length>0&&(
              <>
                <button
                  disabled={historyIdx<0?history.length===0:historyIdx<=0}
                  onClick={function(){
                    var idx=historyIdx<0?history.length-2:historyIdx-1;
                    if(idx<0)return;
                    setHistoryIdx(idx);
                    setGs(clone(history[idx].gs));
                    setPhase('player_turn');
                    setMsg('⏮ Rewound to turn '+idx);
                  }}
                  title="Rewind one step"
                  style={{background:'rgba(250,204,21,.08)',border:'1px solid rgba(250,204,21,.2)',color:historyIdx===0?'#333':'#facc15',borderRadius:5,padding:'2px 7px',cursor:'pointer',fontSize:11,fontWeight:700,opacity:historyIdx===0?0.3:1}}>
                  ←
                </button>
                <button
                  disabled={historyIdx<0}
                  onClick={function(){
                    if(historyIdx<0)return;
                    var idx=historyIdx+1;
                    if(idx>=history.length){
                      setHistoryIdx(-1);
                      setGs(clone(history[history.length-1].gs));
                      setPhase('player_turn');
                      setMsg('');
                    } else {
                      setHistoryIdx(idx);
                      setGs(clone(history[idx].gs));
                      setPhase('player_turn');
                    }
                  }}
                  title="Step forward"
                  style={{background:'rgba(250,204,21,.08)',border:'1px solid rgba(250,204,21,.2)',color:historyIdx<0?'#333':'#facc15',borderRadius:5,padding:'2px 7px',cursor:'pointer',fontSize:11,fontWeight:700,opacity:historyIdx<0?0.3:1}}>
                  →
                </button>
                {historyIdx>=0&&(
                  <button
                    onClick={function(){setHistoryIdx(-1);setGs(clone(history[history.length-1].gs));setPhase('player_turn');setMsg('');}}
                    title="Return to present"
                    style={{background:'rgba(34,197,94,.1)',border:'1px solid rgba(34,197,94,.3)',color:'#22c55e',borderRadius:5,padding:'2px 6px',cursor:'pointer',fontSize:9,fontWeight:700}}>
                    NOW
                  </button>
                )}
              </>
            )}
          </div>
        </div>
        {historyIdx>=0&&(
          <div style={{padding:'4px 8px',background:'rgba(250,204,21,.06)',borderBottom:'1px solid rgba(250,204,21,.15)',fontSize:9,color:'#facc15',fontWeight:600}}>
            ⏮ Viewing past state ({historyIdx+1}/{history.length}) — read only
          </div>
        )}
        <div style={S.logSidebarScroll} ref={logRef}>
          {log.map(function(e,i){
            var isKO=e.includes('KO')||e.includes('prize')||e.includes('Win')||e.includes('Prize');
            var isAI=e.startsWith('🤖')||e.startsWith('—')||e.startsWith('AI');
            var isDraw=e.startsWith('📥');
            var isSystem=e.includes('Game begins')||e.includes('placed');
            var isSnap=history.some(function(h){return h.logEntry===e;});
            var c=isKO?'#facc15':isAI?'#f97316':isDraw?'#3b82f6':isSystem?'#555':'#666';
            var bg=isSnap?'rgba(250,204,21,.04)':'transparent';
            return(
              <div key={i}
                title={isSnap?'Click to rewind here':''}
                onClick={isSnap?function(){
                  var snapIdx=history.findIndex(function(h){return h.logEntry===e;});
                  if(snapIdx>=0){setHistoryIdx(snapIdx);setGs(clone(history[snapIdx].gs));setPhase('player_turn');setMsg('⏮ Rewound');}
                }:undefined}
                style={Object.assign({},S.logSidebarEntry,{color:c,background:bg,cursor:isSnap?'pointer':'default',borderLeft:isSnap?'2px solid rgba(250,204,21,.4)':'2px solid transparent',paddingLeft:6})}>
                {e}
              </div>
            );
          })}
        </div>
      </div>

      </div> {/* /body */}

      {/* ZOOM MODAL */}
      {zoomCard&&<ZoomModal card={zoomCard} onClose={function(){setZoom(null);}}/>}

      {/* ── CONFIRM DIALOG ── */}
      {confirm&&(
        <div onClick={confirm.onNo} style={{position:'fixed',inset:0,zIndex:2000,background:'rgba(0,0,0,.75)',backdropFilter:'blur(8px)',display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
          <div onClick={function(e){e.stopPropagation();}} style={{background:'#1a1a24',border:'1px solid rgba(255,255,255,.12)',borderRadius:16,padding:24,maxWidth:320,width:'100%',boxShadow:'0 20px 60px rgba(0,0,0,.6)'}}>
            <div style={{fontSize:13,color:'#aaa',fontWeight:600,letterSpacing:'0.08em',textTransform:'uppercase',marginBottom:8}}>Confirm action</div>
            <div style={{fontFamily:'var(--font-display)',fontWeight:700,fontSize:18,color:'#fff',marginBottom:20,lineHeight:1.3}}>{confirm.msg}</div>
            <div style={{display:'flex',gap:10}}>
              <button onClick={confirm.onNo}
                style={{flex:1,padding:'10px 0',borderRadius:10,fontSize:13,fontWeight:700,cursor:'pointer',border:'1px solid rgba(255,255,255,.12)',background:'rgba(255,255,255,.04)',color:'#aaa',fontFamily:'var(--font-ui)'}}>
                Cancel
              </button>
              <button onClick={confirm.onYes}
                style={{flex:1,padding:'10px 0',borderRadius:10,fontSize:13,fontWeight:700,cursor:'pointer',border:'1px solid #facc15',background:'rgba(250,204,21,.12)',color:'#facc15',fontFamily:'var(--font-ui)'}}>
                Yes, play it
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin{to{transform:rotate(360deg)}} *{box-sizing:border-box} ::-webkit-scrollbar{width:3px;height:3px} ::-webkit-scrollbar-thumb{background:rgba(250,204,21,.3);border-radius:3px} button:focus{outline:none} [draggable]{-webkit-user-drag:element;}`}</style>
    </div>
  );
}

function Counter({label,value,color}){
  return<div style={{background:'rgba(255,255,255,.04)',border:'1px solid rgba(255,255,255,.07)',borderRadius:7,padding:'4px 8px',textAlign:'center',minWidth:40}}><div style={{fontSize:8,color:'#444',letterSpacing:1}}>{label}</div><div style={{fontSize:18,fontWeight:900,color:color||'#fff'}}>{value}</div></div>;
}

var S={
  root:{background:'#07070f',height:'100vh',color:'#fff',fontFamily:'sans-serif',display:'flex',flexDirection:'column',overflow:'hidden',WebkitOverflowScrolling:'touch'},
  // Main body: play area + sidebar log
  body:{flex:1,display:'flex',overflow:'hidden',minHeight:0},
  // Left: play area (mat + hand)
  playCol:{flex:1,display:'flex',flexDirection:'column',overflow:'hidden',minHeight:0},
  mat:{flex:1,overflowY:'auto',display:'flex',flexDirection:'column',alignItems:'center',padding:'6px 8px',gap:5,width:'100%'},
  matInner:{width:'100%',maxWidth:760,display:'flex',flexDirection:'column',gap:6},
  field:{background:'rgba(255,255,255,.02)',border:'1px solid rgba(255,255,255,.06)',borderRadius:12,padding:'8px 10px',width:'100%'},
  fieldLabel:{fontSize:10,color:'#444',marginBottom:4,fontWeight:600},
  fieldRow:{display:'flex',gap:8,alignItems:'flex-start',flexWrap:'wrap',justifyContent:'center'},
  handArea:{background:'rgba(255,255,255,.02)',borderTop:'1px solid rgba(255,255,255,.06)',padding:'6px 8px',flexShrink:0},
  handRow:{display:'flex',gap:5,overflowX:'auto',paddingBottom:3,alignItems:'flex-end',minHeight:96,justifyContent:'center'},
  // Right: battle log sidebar
  logSidebar:{width:220,background:'#0a0a14',borderLeft:'1px solid rgba(255,255,255,.07)',display:'flex',flexDirection:'column',flexShrink:0},
  logSidebarHdr:{padding:'8px 10px',borderBottom:'1px solid rgba(255,255,255,.06)',display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0},
  logSidebarScroll:{flex:1,overflowY:'auto',padding:'4px 6px',display:'flex',flexDirection:'column',gap:1},
  logSidebarEntry:{fontSize:10,padding:'3px 4px',borderRadius:4,cursor:'default',borderBottom:'1px solid rgba(255,255,255,.03)'},
  // Legacy (kept for compat)
  logArea:{display:'none'}, // hidden — replaced by sidebar
  logScroll:{height:55,overflowY:'auto',display:'flex',flexDirection:'column',gap:1},
  hdr:{background:'#0a0a18',borderBottom:'1px solid rgba(250,204,21,.1)',padding:'8px 14px',display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0,gap:8},
  bY:{background:'rgba(250,204,21,.1)',border:'1px solid #facc15',color:'#facc15',borderRadius:7,padding:'5px 10px',cursor:'pointer',fontSize:12,fontWeight:700},
  bG:{background:'rgba(255,255,255,.05)',border:'1px solid rgba(255,255,255,.12)',color:'#aaa',borderRadius:7,padding:'5px 10px',cursor:'pointer',fontSize:12,fontWeight:700},
  bB:{background:'rgba(59,130,246,.1)',border:'1px solid #3b82f6',color:'#3b82f6',borderRadius:7,padding:'5px 10px',cursor:'pointer',fontSize:12,fontWeight:700},
};
